import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Eye } from "lucide-react";
import { Input } from "../components/ui/input";
import { listIds, deleteId, fileUrl, errMsg } from "../lib/api";
import { UncertaintyBadge } from "../components/ResultView";
import { MarkdownCell, CodeCell } from "../components/Cell";

const FILTERS = [["all", "all"], ["confident", "accepted"], ["uncertain", "abstained"]];

export default function HistoryPage({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    const t = setTimeout(() => listIds({ q: q || undefined, status: status === "all" ? undefined : status }).then(setRows).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q, status]);

  const remove = async (id) => {
    try { await deleteId(id); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Record removed"); } catch (e) { toast.error(errMsg(e)); }
  };
  const filt = [q && `scientific_name.str.contains("${q}")`, status !== "all" && `status == "${status}"`].filter(Boolean).join(" & ");

  return (
    <div>
      <MarkdownCell><h1>Identification history</h1><p>Every run is persisted with its predictions, uncertainty metrics and GBIF / iNaturalist records.</p></MarkdownCell>
      <CodeCell n={1} code={`df = db.identifications.to_dataframe()\ndf${filt ? `[${filt}]` : ""}.sort_values("created_at", ascending=False)`} testid="history-cell">
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <Input data-testid="history-search-input" className="font-mono text-sm sm:max-w-xs" placeholder="filter species…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div data-testid="history-uncertainty-filter" className="flex rounded border p-0.5 bg-secondary w-fit">
            {FILTERS.map(([v, l]) => (
              <button key={v} data-testid={`history-filter-${v}`} onClick={() => setStatus(v)} className={`px-3 py-1 rounded-sm text-xs font-mono transition-colors ${status === v ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div data-testid="history-table-container" className="overflow-x-auto">
          {rows.length === 0 ? <div data-testid="history-empty" className="font-mono text-xs text-muted-foreground px-2">Empty DataFrame · Columns: [image, scientific_name, p, H_norm, decision]</div> : (
            <table className="text-[13px] w-full min-w-[640px]">
              <thead><tr className="border-b text-right font-semibold"><th className="px-2 py-1"></th><th className="px-2 py-1 text-left">image</th><th className="px-2 py-1 text-left">scientific_name</th><th className="px-2 py-1 text-left">true_label</th><th className="px-2 py-1">p</th><th className="px-2 py-1">H_norm</th><th className="px-2 py-1 text-left">decision</th><th className="px-2 py-1 text-left">created_at</th><th></th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`history-row-${r.id}`} className="odd:bg-secondary/70 text-right hover:bg-primary/5">
                    <td className="px-2 py-1 font-semibold">{i}</td>
                    <td className="px-2 py-1 text-left"><img src={fileUrl(r.image_path)} alt="" loading="lazy" className="h-10 w-10 rounded-sm object-cover bg-secondary" /></td>
                    <td className="px-2 py-1 text-left italic">{r.predictions?.[0]?.scientific_name}</td>
                    <td className="px-2 py-1 text-left italic text-muted-foreground">{r.true_label || "NaN"}</td>
                    <td className="px-2 py-1 font-mono">{r.predictions?.[0]?.probability?.toFixed(3)}</td>
                    <td className="px-2 py-1 font-mono">{r.normalized_entropy?.toFixed(3)}</td>
                    <td className="px-2 py-1 text-left"><UncertaintyBadge uncertain={r.uncertain} /></td>
                    <td className="px-2 py-1 text-left font-mono text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <button data-testid={`history-open-${r.id}`} onClick={() => onOpen(r.id)} className="p-1 hover:text-primary"><Eye className="h-4 w-4" /></button>
                      <button data-testid={`history-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {rows.length > 0 && <div className="font-mono text-xs text-muted-foreground mt-2 px-2">{rows.length} rows × 8 columns</div>}
        </div>
      </CodeCell>
    </div>
  );
}
